import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, ColorType } from 'lightweight-charts';
import { getTickerOHLCV, getTickerCompanyInfo } from '../api';
import { usePersistentPageSize } from '../hooks/usePersistentPageSize';
import { useLocalStorage } from '../hooks/useLocalStorage';
import StrategyParamsLabel from './StrategyParamsLabel';
import { halfYearBars } from '../lib/tradeContext';

// ── Light theme (matching DataTab) ────────────────────────────────────────
const C = {
  pageBg:   '#F1F5F9',
  cardBg:   '#FFFFFF',
  border:   '#E2E8F0',
  borderMd: '#CBD5E1',
  text:     '#0F172A',
  textMd:   '#475569',
  textSm:   '#94A3B8',
  primary:  '#2563EB',
  success:  '#16A34A',
  danger:   '#DC2626',
  warning:  '#D97706',
  shadow:   '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.10)',
};

const PERIODS = ['1M', '3M', '6M', 'YTD', '1Y', '2Y', 'All'];

function getFromDate(period) {
  const now = new Date();
  switch (period) {
    case '1M':  return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    case '3M':  return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    case '6M':  return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    case 'YTD': return new Date(now.getFullYear(), 0, 1);
    case '1Y':  return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    case '2Y':  return new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
    default:    return null;
  }
}

function calcMA(data, period) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j].close;
    result.push({ time: data[i]._chartTime, value: sum / period });
  }
  return result;
}

// ── Timezone handling ───────────────────────────────────────────────────────
// The chart axis renders in market time (ET). lightweight-charts formats the time
// axis in UTC, so we feed every timestamp as its *ET wall-clock interpreted as UTC*.
// Two input shapes reach us and MUST land in the same frame or markers drift off
// their bars (see the +2h LUNR-buy drift bug):
//   • bars  — tz-naive ET from the OHLCV endpoint ("2026-07-27T15:00:00"), already
//             the ET wall clock → treat directly as UTC.
//   • trades— absolute instants with an offset ("2026-07-27T10:04:28-04:00") → must
//             be projected into America/New_York first (DST-safe via Intl).
const _ET_DTF = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});
function _etParts(instantMs) {
  const p = {};
  for (const part of _ET_DTF.formatToParts(new Date(instantMs))) {
    if (part.type !== 'literal') p[part.type] = part.value;
  }
  let h = parseInt(p.hour, 10);
  if (h === 24) h = 0; // some engines emit "24" for midnight under hour12:false
  return { y: +p.year, mo: +p.month, d: +p.day, h, mi: +p.minute, s: +p.second };
}
const _HAS_TZ = /(?:Z|[+-]\d{2}:?\d{2})$/;
// Seconds since epoch for the ET wall-clock of `dateStr`, interpreted as UTC.
function etWallSeconds(dateStr) {
  const s = String(dateStr);
  if (!_HAS_TZ.test(s)) return Math.floor(Date.parse(s + 'Z') / 1000); // naive ET → as-UTC
  const p = _etParts(Date.parse(s));                                   // instant → ET wall
  return Math.floor(Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) / 1000);
}
// Same as etWallSeconds but for a date-only bound ("YYYY-MM-DD" [+ "T23:59:59"]).
function boundSeconds(dateStr) {
  const s = String(dateStr);
  return Math.floor(Date.parse(s.length <= 10 ? s + 'T00:00:00Z' : s + 'Z') / 1000);
}

function toChartTime(dateStr, isHourly) {
  if (!isHourly) return dateStr.slice(0, 10);
  return etWallSeconds(dateStr);
}

function isIntraday(chartType) {
  return chartType === 'hourly' || chartType === '5m' || chartType === '1m';
}

/* Canonical reason key from a backtest OR live reason string. The live bot emits
 * dynamic reasons with an inline % (e.g. "drop_in_window 2.34%", "no_growth +1.5%",
 * "risk_protection: before weekend"), so match on a prefix, not exact equality —
 * otherwise the labels fall through to the raw English code. */
function _reasonKey(reason) {
  const r = String(reason || '').toLowerCase().trim();
  if (!r) return '';
  if (r.startsWith('drop_from_buy'))                      return 'drop';
  if (r.startsWith('drop_in_window') || r.startsWith('drop_in_period')) return 'drop';
  if (r.startsWith('no_growth') || r.startsWith('weak_growth'))         return 'no_growth';
  if (r.startsWith('hard_stop'))                          return 'hard_stop';
  if (r.startsWith('stagnation'))                         return 'stagnation';
  if (r.startsWith('risk_protection') || r.startsWith('overnight') || r.includes('weekend'))
    return 'overnight';
  if (r.startsWith('rotation'))                           return 'rotation';
  if (r.startsWith('opportunity'))                        return 'opportunity';
  if (r.startsWith('take_profit') || r.startsWith('tp_reached')) return 'take_profit';
  if (r.startsWith('signal_window'))                      return 'buy_signal';
  if (r.startsWith('end_of_period'))                      return 'end_of_period';
  return r;
}

const _REASON_SHORT = {
  drop:          'Падіння',
  hard_stop:     'Hard stop',
  no_growth:     'Немає росту',
  stagnation:    'Стагнація',
  overnight:     'Ніч/вих',
  rotation:      'Ротація',
  opportunity:   'Можливість',
  take_profit:   'Take profit',
  buy_signal:    'Сигнал',
  end_of_period: 'Кінець',
};

function formatReason(reason, pnl) {
  const sign = pnl >= 0 ? '+' : '';
  const p    = `${sign}${Number(pnl).toFixed(2)}%`;
  const label = _REASON_SHORT[_reasonKey(reason)];
  return label ? `SELL ${p} · ${label}` : `SELL ${p}`;
}

function formatReasonShort(reason) {
  const key = _reasonKey(reason);
  return _REASON_SHORT[key] || reason || '';
}

function formatReasonFull(reason, pnl) {
  const sign = pnl != null && pnl >= 0 ? '+' : '';
  const p    = pnl != null ? `${sign}${Number(pnl).toFixed(2)}%` : '';
  switch (_reasonKey(reason)) {
    case 'drop':          return `Падіння ${p} (продаж за умовою drop)`;
    case 'hard_stop':     return `Hard stop: ${p} (макс збиток)`;
    case 'no_growth':     return `Немає зростання після T-Buy`;
    case 'stagnation':    return `Стагнація в діапазоні ±X% за N барів`;
    case 'overnight':     return pnl != null && pnl >= 0
      ? `Продаж перед ніччю/вихідними (прибуток ${p})`
      : `Продаж перед ніччю/вихідними (${p})`;
    case 'rotation':      return `Ротація: заміна слабшої позиції`;
    case 'opportunity':   return `Купівля у вільний слот (можливість)`;
    case 'take_profit':   return `Take profit ${p}`;
    case 'end_of_period': return `Кінець періоду`;
    default:              return reason || '—';
  }
}

// Trade time as ET wall-clock "YYYY-MM-DD HH:MM" (market time — the canonical zone).
function formatDateTime(timeStr) {
  if (!timeStr) return '—';
  const s = String(timeStr);
  const p = _HAS_TZ.test(s)
    ? _etParts(Date.parse(s))
    : { y: +s.slice(0, 4), mo: +s.slice(5, 7), d: +s.slice(8, 10),
        h: +s.slice(11, 13), mi: +s.slice(14, 16) };
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.y}-${pad(p.mo)}-${pad(p.d)} ${pad(p.h)}:${pad(p.mi)}`;
}

// Same instant in Kyiv time, "HH:MM" — shown as a secondary reference in tooltips.
const _KYIV_DTF = new Intl.DateTimeFormat('uk-UA', {
  timeZone: 'Europe/Kyiv', hour12: false, hour: '2-digit', minute: '2-digit',
});
function formatKyivTime(timeStr) {
  if (!timeStr) return '';
  try { return _KYIV_DTF.format(new Date(timeStr)); } catch (_) { return ''; }
}

// Ліва межа історії графіка. Сховище барів починається саме тут (виміряно:
// 2020-01-02 — перший бар у 2226 тікерів), тож просити раніше нічого не дає.
const CHART_HISTORY_START = '2020-01-01';

// Compute visible range for a given period preset
function periodToRange(preset, isHourly) {
  const fromDate = getFromDate(preset);
  if (!fromDate) return null; // 'All' → fitContent
  const now = new Date();
  if (isHourly) {
    return {
      from: Math.floor(fromDate.getTime() / 1000),
      to:   Math.floor(now.getTime() / 1000),
    };
  }
  return {
    from: fromDate.toISOString().slice(0, 10),
    to:   now.toISOString().slice(0, 10),
  };
}

// Compute visible range for trade zoom.
// When tradeContext.warmupBars > 0 and filteredData is supplied, uses bar-index
// anchoring: [firstBuy - preBars, lastSell + warmupBars], clamped to data.
// preBars = max(strategy lookback, half a year) — enough history before the entry
// to see what the buy filters (dip window & co) were looking at.
function tradeToRange(tradeContext, isHourly, filteredData) {
  if (!tradeContext) return null;

  const warmupBars = tradeContext.warmupBars ?? 0;
  const preBars    = Math.max(warmupBars, halfYearBars(tradeContext.dataType));
  if (warmupBars > 0 && filteredData?.length > 0) {
    const allTrades = tradeContext.allTickerTrades || [];
    const buys  = allTrades.filter(t => t.action === 'BUY').sort((a, b) => new Date(a.time) - new Date(b.time));
    const sells = allTrades.filter(t => t.action === 'SELL').sort((a, b) => new Date(a.time) - new Date(b.time));

    const toChartT = (isoStr) => isHourly ? etWallSeconds(isoStr) : isoStr.slice(0, 10);

    let buyIdx = 0, sellIdx = filteredData.length - 1;

    if (buys[0]?.time) {
      const buyT = toChartT(buys[0].time);
      const idx  = filteredData.findIndex(d => String(d._chartTime) >= String(buyT));
      if (idx >= 0) buyIdx = idx;
    }

    if (sells.length > 0) {
      const lastSellT = toChartT(sells[sells.length - 1].time);
      let idx = filteredData.length - 1;
      while (idx > 0 && String(filteredData[idx]._chartTime) > String(lastSellT)) idx--;
      sellIdx = idx;
    }

    const fromIdx = Math.max(0, buyIdx - preBars);
    const toIdx   = Math.min(filteredData.length - 1, sellIdx + warmupBars);
    return { from: filteredData[fromIdx]._chartTime, to: filteredData[toIdx]._chartTime };
  }

  // Fallback: date-range zoom
  if (!tradeContext.fromDate || !tradeContext.toDate) return null;
  if (isHourly) {
    return {
      from: boundSeconds(tradeContext.fromDate),
      to:   boundSeconds(tradeContext.toDate + 'T23:59:59'),
    };
  }
  return { from: tradeContext.fromDate, to: tradeContext.toDate };
}

// Visible range for the FULL backtest period: [period start − warmup, period end].
// tradeContext.fromDate/toDate already carry those bounds (set in navigateByTicker).
function periodFullToRange(tradeContext, isHourly) {
  if (!tradeContext?.fromDate || !tradeContext?.toDate) return null;
  if (isHourly) {
    return {
      from: boundSeconds(tradeContext.fromDate),
      to:   boundSeconds(tradeContext.toDate + 'T23:59:59'),
    };
  }
  return { from: tradeContext.fromDate, to: tradeContext.toDate };
}

const S = {
  page:    { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text },
  wrap:    { maxWidth: 1280, margin: '0 auto', padding: '24px 28px' },
  header:  { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
    background: C.cardBg, color: C.textMd, fontSize: 13, cursor: 'pointer',
    fontWeight: 500, boxShadow: C.shadow,
  },
  tickerBadge: {
    fontSize: 22, fontWeight: 800, color: C.text, fontFamily: 'monospace', letterSpacing: '-0.5px',
  },
  companyName: { fontSize: 15, color: C.textMd, fontWeight: 500 },
  tagBadge: (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    background: color === 'blue' ? '#EFF6FF' : color === 'green' ? '#DCFCE7' : '#F1F5F9',
    color: color === 'blue' ? C.primary : color === 'green' ? C.success : C.textSm,
    border: `1px solid ${color === 'blue' ? '#BFDBFE' : color === 'green' ? '#BBF7D0' : C.border}`,
  }),
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  infoCard: (accent) => ({
    background: C.cardBg, border: `1px solid ${C.border}`, borderLeft: `4px solid ${accent}`,
    borderRadius: 10, padding: '12px 16px', boxShadow: C.shadow,
  }),
  infoTitle: (color) => ({ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }),
  infoBody:  { fontSize: 13, color: C.text },
  infoSub:   { fontSize: 12, color: C.textSm, marginTop: 2 },
  controlBar: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  switcher: { display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: C.shadow },
  switchBtn: (active) => ({
    padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? C.primary : C.cardBg,
    color: active ? '#fff' : C.textMd,
    transition: 'all 0.15s',
  }),
  switchBtnDisabled: {
    padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'not-allowed', border: 'none',
    background: C.pageBg, color: C.textSm, opacity: 0.6,
  },
  periodBtn: (active) => ({
    padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.border}`,
    borderRadius: 6, background: active ? C.primary : C.cardBg, color: active ? '#fff' : C.textMd,
    transition: 'all 0.15s',
  }),
  tradeBtn: (active) => ({
    padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    borderRadius: 6, border: `1px solid ${active ? '#D97706' : C.border}`,
    background: active ? '#D97706' : C.cardBg,
    color: active ? '#fff' : C.warning,
    transition: 'all 0.15s',
  }),
  card: { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden' },
  chartHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}` },
  chartTitle: { fontSize: 13, fontWeight: 600, color: C.text },
  maRow: { display: 'flex', gap: 16, alignItems: 'center' },
  maLabel: (color) => ({ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.textMd, cursor: 'pointer' }),
  maDot: (color) => ({ width: 10, height: 3, borderRadius: 2, background: color }),
  chartContainer: { padding: '0', position: 'relative', display: 'flex', flexDirection: 'column' },
  loadingOverlay: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.85)', zIndex: 10, fontSize: 14, color: C.textMd,
  },
  errorMsg: { padding: 40, textAlign: 'center', color: C.danger, fontSize: 14 },
};

export default function TickerDetail({ ticker, onBack, tradeContext, compact = false }) {
  const hasTradeCtx = !!tradeContext;

  const [chartType,   setChartType]   = useState(() => {
    if (tradeContext?.dataType === 'hourly' && ticker.hourly?.has_data) return 'hourly';
    if (tradeContext?.dataType === '5m' && ticker.minute_5m?.has_data) return '5m';
    if (tradeContext?.dataType === '1m' && ticker.minute_1m?.has_data) return '1m';
    return ticker.daily?.has_data ? 'daily' : (ticker.hourly?.has_data ? 'hourly' : 'daily');
  });
  const [displayType, setDisplayType] = useLocalStorage('td_displayType', 'candlestick');
  // period: used for data filtering (normal mode) and active button highlight
  const [period,      setPeriod]      = useState('All');
  // zoomPreset: 'period' | 'trade' | a PERIODS value | 'All' — controls visible range
  // When tradeContext: starts on the full period (start−warmup → end); the "Угода"
  // button zooms to the trade bracket. Otherwise follows the period dropdown.
  const [zoomPreset,  setZoomPreset]  = useState(hasTradeCtx ? 'period' : 'All');
  const [showMA20,    setShowMA20]    = useLocalStorage('td_showMA20', false);
  const [showMA50,    setShowMA50]    = useLocalStorage('td_showMA50', false);
  const [showMA200,   setShowMA200]   = useLocalStorage('td_showMA200', false);
  const [showLabels,  setShowLabels]  = useLocalStorage('td_showLabels', true);
  const [rawData,      setRawData]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [companyInfo,  setCompanyInfo]  = useState(null);

  const [activePairIdx, setActivePairIdx] = useState(-1);
  const [pairsPage, setPairsPage]         = useState(0);
  const [pairsPageSize, setPairsPageSize] = usePersistentPageSize('pg_ticker', 50);
  const [sortCol, setSortCol]             = useLocalStorage('td_sortCol', 'id');
  const [sortDir, setSortDir]             = useLocalStorage('td_sortDir', 'asc');

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPairsPage(0);
  };

  // ── Build trade pairs from allTickerTrades ────────────────────────────────
  const tradePairs = useMemo(() => {
    const allTrades = tradeContext?.allTickerTrades;
    if (!allTrades?.length) return [];
    const sorted = [...allTrades].sort((a, b) => new Date(a.time) - new Date(b.time));
    const pairs = [];
    let pendingBuy = null;
    sorted.forEach(t => {
      if (t.action === 'BUY') {
        pendingBuy = t;
      } else if (t.action === 'SELL' && pendingBuy) {
        pairs.push({ buyTrade: pendingBuy, sellTrade: t, pnl: t.pnl_pct, pnlUsd: t.change_usd, reason: t.description });
        pendingBuy = null;
      }
    });
    if (pendingBuy) {
      pairs.push({ buyTrade: pendingBuy, sellTrade: null, pnl: null, pnlUsd: null, reason: 'open' });
    }
    return pairs;
  }, [tradeContext]);

  // ── Summary stats for all closed pairs ───────────────────────────────────
  const summary = useMemo(() => {
    const closed = tradePairs.filter(p => p.sellTrade);
    if (!closed.length) return null;

    const fmtMins = (totalMins) => {
      if (!totalMins || !isFinite(totalMins) || totalMins <= 0) return '—';
      const d = Math.floor(totalMins / 1440);
      const h = Math.floor((totalMins % 1440) / 60);
      const m = Math.floor(totalMins % 60);
      return [d && `${d}д`, h && `${h}г`, m && `${m}хв`].filter(Boolean).join(' ') || '—';
    };

    const totalPnlUsd = closed.reduce((sum, p) => {
      const v = parseFloat(p.pnlUsd ?? 0);
      return sum + (isNaN(v) ? 0 : v);
    }, 0);

    const pnlPcts = closed.map(p => parseFloat(p.pnl ?? 0)).filter(v => !isNaN(v));
    const avgPnlPct = pnlPcts.length ? pnlPcts.reduce((a, b) => a + b, 0) / pnlPcts.length : 0;

    const durationsMins = closed.map(p => {
      if (!p.buyTrade?.time || !p.sellTrade?.time) return null;
      const diff = new Date(p.sellTrade.time) - new Date(p.buyTrade.time);
      return diff > 0 ? Math.floor(diff / 60000) : null;
    }).filter(v => v != null && v > 0);

    const avgDurMin = durationsMins.length
      ? durationsMins.reduce((a, b) => a + b, 0) / durationsMins.length : 0;
    const maxDurMin = durationsMins.length ? Math.max(...durationsMins) : 0;
    const minDurMin = durationsMins.length ? Math.min(...durationsMins) : 0;

    const wins = closed.filter(p => parseFloat(p.pnlUsd ?? 0) > 0).length;
    return {
      total:      closed.length,
      wins,
      losses:     closed.length - wins,
      totalPnlUsd,
      avgPnlPct,
      avgDur:     fmtMins(Math.round(avgDurMin)),
      maxDur:     fmtMins(maxDurMin),
      minDur:     fmtMins(minDurMin),
    };
  }, [tradePairs]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartCardRef         = useRef(null);
  const chartContainerRef    = useRef(null);
  const chartInnerRef        = useRef(null);
  const labelZoneRef         = useRef(null);
  const labelZoneHeightRef   = useRef(0);
  const chartRef             = useRef(null);
  const markersRef           = useRef([]);
  const tooltipRef           = useRef(null);
  const mainSeriesRef        = useRef(null);
  const renderTradeLabelsRef = useRef(null);
  const showLabelsRef          = useRef(true);
  // Store the zoom preset in a ref so zoom-only effect can read it without being a dep
  const zoomPresetRef          = useRef(zoomPreset);
  useEffect(() => { zoomPresetRef.current = zoomPreset; }, [zoomPreset]);
  useEffect(() => { showLabelsRef.current = showLabels; }, [showLabels]);
  // Track latest filteredData in a ref so the zoom-only effect can access it
  const filteredDataRef = useRef([]);

  // ── Fetch company info once ────────────────────────────────────────────────
  useEffect(() => {
    getTickerCompanyInfo(ticker.ticker)
      .then(r => setCompanyInfo(r.data))
      .catch(() => setCompanyInfo(null));
  }, [ticker.ticker]);

  // ── Fetch data; pass from_date when tradeContext exists to ensure BUY bar is included ─
  useEffect(() => {
    setLoading(true);
    setError('');
    setRawData([]);
    // Історія тягнеться ЗАВЖДИ повна: від 2020-01-01 до останнього наявного
    // бара тікера. Раніше тут стояла дата угоди мінус 5 днів — і графік просто
    // не мав чого показати лівіше за неї, хоч би як його віддаляли. Праву межу
    // не задаємо взагалі: у делістингових паперів дані обриваються в минулому,
    // тож «сьогодні мінус два дні» відрізало б їм хвіст або малювало порожнечу.
    //
    // Обмеження MAX_BARS=5000 на бекенді діє ЛИШЕ коли `from` не заданий, тож
    // явна дата його знімає (годинних барів за шість років ~11 тис.).
    const fromDate = (tradeContext?.fromDate && tradeContext.fromDate < CHART_HISTORY_START)
      ? tradeContext.fromDate
      : CHART_HISTORY_START;
    getTickerOHLCV(ticker.ticker, chartType, fromDate)
      .then(r => setRawData(r.data))
      .catch(() => setError('Не вдалося завантажити дані'))
      .finally(() => setLoading(false));
  }, [ticker.ticker, chartType, tradeContext?.fromDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter data client-side ────────────────────────────────────────────────
  // When tradeContext is present: always use ALL data (zoom handles the view).
  // When normal: filter by the selected period.
  const filteredData = useMemo(() => {
    const effectivePeriod = hasTradeCtx ? 'All' : period;
    const fromDate = getFromDate(effectivePeriod);
    const isHourly = isIntraday(chartType);

    let data = rawData
      .filter(d => !fromDate || new Date(d.date) >= fromDate)
      .map(d => ({ ...d, _chartTime: toChartTime(d.date, isHourly) }));

    data.sort((a, b) => (a._chartTime < b._chartTime ? -1 : a._chartTime > b._chartTime ? 1 : 0));

    const seen = new Set();
    const deduped = [];
    for (let i = data.length - 1; i >= 0; i--) {
      const key = String(data[i]._chartTime);
      if (!seen.has(key)) { seen.add(key); deduped.unshift(data[i]); }
    }

    // Live same-day trades: the parquet chart data lags ~1 day (nightly cron), so
    // a trade executed today sits BEYOND the last price bar → its markers/labels
    // have no X-anchor and can't render (though the table shows them fine). Append
    // a synthetic doji bar at each trade time that falls after the last loaded bar,
    // using the REAL executed price, so the existing markers + labels attach. No-op
    // for historical/backtest trades (their times are always within the series).
    if (hasTradeCtx && deduped.length) {
      const lastT = deduped[deduped.length - 1]._chartTime;
      const extra = [];
      for (const t of (tradeContext?.allTickerTrades || [])) {
        if (t?.price == null || t?.time == null) continue;
        const ct = toChartTime(t.time, isHourly);
        if (ct > lastT && !seen.has(String(ct))) {
          seen.add(String(ct));
          const p = Number(t.price);
          extra.push({ date: t.time, _chartTime: ct, open: p, high: p, low: p, close: p, _synthetic: true });
        }
      }
      if (extra.length) {
        extra.sort((a, b) => (a._chartTime < b._chartTime ? -1 : 1));
        return [...deduped, ...extra];
      }
    }
    return deduped;
  }, [rawData, period, chartType, hasTradeCtx, tradeContext]);
  useEffect(() => { filteredDataRef.current = filteredData; }, [filteredData]);

  // ── Helper: apply zoom to chart ───────────────────────────────────────────
  const applyZoom = useCallback((chart, preset, isHourly, fd) => {
    if (preset === 'trade') {
      const range = tradeToRange(tradeContext, isHourly, fd);
      if (range) {
        try { chart.timeScale().setVisibleRange(range); } catch (_) {}
      } else {
        chart.timeScale().fitContent();
      }
    } else if (preset === 'period') {
      const range = periodFullToRange(tradeContext, isHourly);
      if (range) {
        try { chart.timeScale().setVisibleRange(range); } catch (_) {}
      } else {
        chart.timeScale().fitContent();
      }
    } else {
      const range = periodToRange(preset, isHourly);
      if (range) {
        try { chart.timeScale().setVisibleRange(range); } catch (_) {}
      } else {
        chart.timeScale().fitContent();
      }
    }
  }, [tradeContext]);

  // ── Zoom to a specific trade pair ─────────────────────────────────────────
  const zoomToPair = useCallback((idx, pairs) => {
    setActivePairIdx(idx);
    if (!chartRef.current || idx < 0 || !pairs || idx >= pairs.length) return;
    const pair = pairs[idx];
    const isHourly = isIntraday(chartType);
    const from = new Date(pair.buyTrade.time);
    const to   = new Date(pair.sellTrade?.time || pair.buyTrade.time);
    from.setDate(from.getDate() - 3);
    to.setDate(to.getDate() + 3);
    try {
      chartRef.current.timeScale().setVisibleRange(
        isHourly
          ? { from: Math.floor(from.getTime() / 1000), to: Math.floor(to.getTime() / 1000) }
          : { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
      );
    } catch (_) {}
  }, [chartType]);

  // ── Create/recreate chart when data or display settings change ─────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    if (filteredData.length === 0) return;

    const container = chartContainerRef.current;
    const isHourly  = isIntraday(chartType);

    if (!chartInnerRef.current) return;

    const chart = createChart(chartInnerRef.current, {
      width:  container.clientWidth,
      height: 420,
      layout: {
        background: { type: ColorType.Solid, color: '#FFFFFF' },
        textColor: '#0F172A',
        fontSize: 12,
      },
      grid: {
        vertLines: { color: '#F1F5F9' },
        horzLines: { color: '#F1F5F9' },
      },
      rightPriceScale: { borderColor: '#E2E8F0' },
      timeScale: {
        borderColor: '#E2E8F0',
        timeVisible: isHourly,
        secondsVisible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      crosshair: { mode: 1 },
    });

    const priceData = filteredData.map(d => ({
      time:  d._chartTime,
      open:  d.open,
      high:  d.high,
      low:   d.low,
      close: d.close,
    }));

    let mainSeries;
    if (displayType === 'candlestick') {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor:         '#16A34A',
        downColor:       '#DC2626',
        borderUpColor:   '#16A34A',
        borderDownColor: '#DC2626',
        wickUpColor:     '#16A34A',
        wickDownColor:   '#DC2626',
      });
      mainSeries.setData(priceData);
    } else {
      mainSeries = chart.addSeries(LineSeries, { color: '#2563EB', lineWidth: 2 });
      mainSeries.setData(priceData.map(d => ({ time: d.time, value: d.close })));
    }

    // Volume
    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat:  { type: 'volume' },
      priceScaleId: 'vol',
    });
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volSeries.setData(filteredData.map(d => ({
      time:  d._chartTime,
      value: d.volume,
      color: d.close >= d.open ? 'rgba(22,163,74,0.35)' : 'rgba(220,38,38,0.35)',
    })));

    // Moving averages
    for (const { show, period: maPeriod, color, title } of [
      { show: showMA20,  period: 20,  color: '#F59E0B', title: 'MA20'  },
      { show: showMA50,  period: 50,  color: '#8B5CF6', title: 'MA50'  },
      { show: showMA200, period: 200, color: '#EF4444', title: 'MA200' },
    ]) {
      if (!show) continue;
      const s = chart.addSeries(LineSeries, {
        color, lineWidth: 1, title,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      });
      const maData = calcMA(filteredData, maPeriod);
      if (maData.length > 0) s.setData(maData);
    }

    // ── Trade markers ────────────────────────────────────────────────────────
    if (tradeContext && filteredData.length > 0) {
      const toTime = (isoStr) => {
        if (!isoStr) return null;
        return isHourly ? etWallSeconds(isoStr) : isoStr.slice(0, 10);
      };
      const findClosest = (t) => filteredData.reduce((best, d) =>
        Math.abs(Number(d._chartTime) - Number(t)) <
        Math.abs(Number(best._chartTime) - Number(t)) ? d : best
      , filteredData[0]);

      if (tradePairs.length > 0) {
        // ── Multi-trade: markers for all pairs ──
        const markers = [];
        tradePairs.forEach((pair, idx) => {
          const pColor  = pair.pnl == null ? '#2563EB' : pair.pnl >= 0 ? '#16A34A' : '#DC2626';
          const bgColor = pair.pnl == null ? 'rgba(37,99,235,0.08)'
                        : pair.pnl >= 0   ? 'rgba(22,163,74,0.08)'
                        :                   'rgba(220,38,38,0.08)';

          // BUY marker
          const buyT = toTime(pair.buyTrade.time);
          if (buyT !== null) {
            const bar = findClosest(buyT);
            if (bar) {
              const px = pair.buyTrade.price != null
                ? ` $${Number(pair.buyTrade.price).toFixed(2)}` : '';
              markers.push({
                time: bar._chartTime, position: 'belowBar',
                color: '#16A34A', shape: 'arrowUp', size: 3,
                text: `#${idx + 1} BUY${px}`,
                _data: {
                  action: 'BUY',
                  idx:    idx + 1,
                  price:  pair.buyTrade.price,
                  time:   pair.buyTrade.time,
                },
              });
            }
          }

          // SELL marker
          if (pair.sellTrade) {
            const sellT = toTime(pair.sellTrade.time);
            if (sellT !== null) {
              const bar = findClosest(sellT);
              if (bar) {
                const pnlStr = pair.pnl != null
                  ? ` ${pair.pnl >= 0 ? '+' : ''}${Number(pair.pnl).toFixed(2)}%` : '';
                markers.push({
                  time: bar._chartTime, position: 'aboveBar',
                  color: pColor, shape: 'arrowDown', size: 3,
                  text: `#${idx + 1} SELL${pnlStr}`,
                  _data: {
                    action:   'SELL',
                    idx:      idx + 1,
                    price:    pair.sellTrade.price,
                    buyPrice: pair.buyTrade.price,
                    pnl:      pair.pnl,
                    pnlUsd:   pair.pnlUsd,
                    reason:   pair.reason,
                    time:     pair.sellTrade.time,
                    duration: (() => {
                      if (!pair.buyTrade?.time || !pair.sellTrade?.time) return null;
                      const diff = new Date(pair.sellTrade.time) - new Date(pair.buyTrade.time);
                      if (diff <= 0) return null;
                      const mins = Math.floor(diff / 60000);
                      const hrs  = Math.floor(mins / 60);
                      const days = Math.floor(hrs / 24);
                      if (days >= 1) return `${days}д ${hrs % 24}г`;
                      if (hrs  >= 1) return `${hrs}г ${mins % 60}хв`;
                      return `${mins}хв`;
                    })(),
                  },
                });
              }
            }
          }

          // ── Background shading for the trade period ──
          if (pair.buyTrade?.time && pair.sellTrade?.time) {
            const bT = toTime(pair.buyTrade.time);
            const sT = toTime(pair.sellTrade.time);
            if (bT !== null && sT !== null) {
              const bBar = findClosest(bT);
              const sBar = findClosest(sT);
              if (bBar && sBar) {
                const bIdx = filteredData.findIndex(d => String(d._chartTime) === String(bBar._chartTime));
                const sIdx = filteredData.findIndex(d => String(d._chartTime) === String(sBar._chartTime));
                if (bIdx !== -1 && sIdx !== -1 && sIdx >= bIdx) {
                  const scaleId = `bg_${idx}`;
                  const bgSeries = chart.addSeries(HistogramSeries, {
                    color: bgColor,
                    priceFormat: { type: 'volume' },
                    priceScaleId: scaleId,
                  });
                  chart.priceScale(scaleId).applyOptions({ scaleMargins: { top: 0, bottom: 0 }, visible: false });
                  bgSeries.setData(
                    filteredData.slice(bIdx, sIdx + 1).map(d => ({ time: d._chartTime, value: 1 }))
                  );
                }
              }
            }
          }
        });

        if (markers.length > 0 && mainSeries) {
          markers.sort((a, b) => String(a.time) < String(b.time) ? -1 : 1);
          try { mainSeries.setMarkers(markers); } catch (_) {}
          markersRef.current = markers;
        }

        // ── Diagonal line: buy price → sell price ──
        tradePairs.forEach(pair => {
          if (!pair.buyTrade?.time || !pair.sellTrade?.time) return;
          if (pair.buyTrade.price == null || pair.sellTrade.price == null) return;
          const buyT  = toTime(pair.buyTrade.time);
          const sellT = toTime(pair.sellTrade.time);
          if (buyT === null || sellT === null) return;
          const buyBar  = findClosest(buyT);
          const sellBar = findClosest(sellT);
          if (!buyBar || !sellBar) return;
          if (String(buyBar._chartTime) === String(sellBar._chartTime)) return;

          const lc = pair.pnl == null ? '#2563EB'
                   : pair.pnl >= 0   ? '#22c55e'
                   :                   '#ef4444';
          const ls = chart.addSeries(LineSeries, {
            color: lc, lineWidth: 2, lineStyle: 0,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          });
          ls.setData([
            { time: buyBar._chartTime,  value: pair.buyTrade.price  },
            { time: sellBar._chartTime, value: pair.sellTrade.price },
          ]);
        });

      } else {
        // ── Legacy single-trade markers (backward compat) ──
        const markers = [];
        if (tradeContext.buyTrade?.time) {
          const bar = findClosest(toTime(tradeContext.buyTrade.time));
          if (bar) {
            const px = tradeContext.buyTrade.price != null
              ? ` $${Number(tradeContext.buyTrade.price).toFixed(2)}` : '';
            markers.push({ time: bar._chartTime, position: 'belowBar', color: '#16A34A', shape: 'arrowUp', text: `BUY${px}` });
          }
        }
        if (tradeContext.sellTrade?.time) {
          const bar = findClosest(toTime(tradeContext.sellTrade.time));
          if (bar) {
            const px  = tradeContext.sellTrade.price != null
              ? ` $${Number(tradeContext.sellTrade.price).toFixed(2)}` : '';
            const pnl = tradeContext.sellTrade.pnl_pct != null
              ? ` (${tradeContext.sellTrade.pnl_pct >= 0 ? '+' : ''}${Number(tradeContext.sellTrade.pnl_pct).toFixed(2)}%)` : '';
            markers.push({ time: bar._chartTime, position: 'aboveBar', color: '#DC2626', shape: 'arrowDown', text: `SELL${px}${pnl}` });
          }
        }
        if (markers.length > 0 && mainSeries) {
          try { mainSeries.setMarkers(markers.sort((a, b) => String(a.time) < String(b.time) ? -1 : 1)); } catch (_) {}
        }
      }
    }

    // ── Apply zoom ───────────────────────────────────────────────────────────
    chart.timeScale().fitContent();
    applyZoom(chart, zoomPresetRef.current, isHourly, filteredData);

    // ── Store main series ref ────────────────────────────────────────────────
    mainSeriesRef.current = mainSeries;

    // ── Trade labels renderer — labels above chart with connecting lines ─────
    const renderTradeLabels = () => {
      const containerEl = chartContainerRef.current;
      const labelZoneEl = labelZoneRef.current;
      if (!containerEl || !labelZoneEl || !mainSeries) return;

      containerEl.querySelectorAll('.trade-label, .trade-line').forEach(el => el.remove());

      if (!showLabelsRef.current || !tradePairs.length) {
        labelZoneEl.style.height = '0px';
        labelZoneHeightRef.current = 0;
        return;
      }

      const LABEL_WIDTH  = 155;
      const ROW_HEIGHT   = 72;
      const LABEL_GAP    = 8;

      // Snap a trade chart-time to its nearest loaded bar. A trade fires mid-bar
      // (e.g. 10:04 between the 10:00 and 11:00 bars), and timeToCoordinate() returns
      // null for a time that isn't on the scale → the label would silently vanish.
      // Snapping to the bar the marker sits on keeps the banner anchored.
      const _fd = filteredDataRef.current || [];
      const snapT = (tval) => {
        if (!isHourly || typeof tval !== 'number' || !_fd.length) return tval;
        let best = _fd[0]._chartTime, bd = Math.abs(Number(_fd[0]._chartTime) - tval);
        for (let i = 1; i < _fd.length; i++) {
          const d = Math.abs(Number(_fd[i]._chartTime) - tval);
          if (d < bd) { bd = d; best = _fd[i]._chartTime; }
        }
        return best;
      };

      // ── 1. Collect visible label positions ──
      const labelPositions = [];
      tradePairs.forEach((pair, idx) => {
        if (!pair.buyTrade?.price) return;

        // BUY
        const buyChartTime = snapT(toChartTime(pair.buyTrade.time, isHourly));
        try {
          const buyTimeCoord  = chart.timeScale().timeToCoordinate(buyChartTime);
          const buyPriceCoord = mainSeries.priceToCoordinate(Number(pair.buyTrade.price));
          if (buyTimeCoord !== null) {
            labelPositions.push({ type: 'buy', idx: idx + 1, x: buyTimeCoord, pointY: buyPriceCoord, pair });
          }
        } catch (_) {}

        // SELL
        if (pair.sellTrade?.price != null) {
          const sellChartTime = snapT(toChartTime(pair.sellTrade.time, isHourly));
          try {
            const sellTimeCoord  = chart.timeScale().timeToCoordinate(sellChartTime);
            const sellPriceCoord = mainSeries.priceToCoordinate(Number(pair.sellTrade.price));
            if (sellTimeCoord !== null) {
              labelPositions.push({ type: 'sell', idx: idx + 1, x: sellTimeCoord, pointY: sellPriceCoord, pair });
            }
          } catch (_) {}
        }
      });

      // ── 2. Sort by X and assign rows to avoid overlap ──
      labelPositions.sort((a, b) => a.x - b.x);
      const rows = []; // rows[r] = [{ x }]
      labelPositions.forEach(pos => {
        let assignedRow = -1;
        for (let r = 0; r < rows.length; r++) {
          const overlaps = rows[r].some(slot => Math.abs(slot.x - pos.x) < LABEL_WIDTH + LABEL_GAP);
          if (!overlaps) { assignedRow = r; break; }
        }
        if (assignedRow === -1) { rows.push([]); assignedRow = rows.length - 1; }
        rows[assignedRow].push({ x: pos.x });
        pos.row = assignedRow;
      });

      // ── 3. Set label zone height ──
      const numRows = Math.max(rows.length, 1);
      const labelZoneHeight = numRows * ROW_HEIGHT + 6;
      labelZoneEl.style.height = labelZoneHeight + 'px';
      labelZoneHeightRef.current = labelZoneHeight;

      // ── 4. Render each label + line + dot ──
      labelPositions.forEach(pos => {
        const { type, idx, x, pointY, pair, row } = pos;
        const isBuy   = type === 'buy';
        const color   = isBuy
          ? '#16a34a'
          : pair.pnl == null ? '#2563EB' : pair.pnl >= 0 ? '#16a34a' : '#dc2626';

        const labelLeft = Math.max(2, Math.min(x - LABEL_WIDTH / 2, containerEl.clientWidth - LABEL_WIDTH - 2));
        const labelTop  = row * ROW_HEIGHT + 4;

        const sign   = pair.pnl != null && pair.pnl >= 0 ? '+' : '';
        const usdStr = pair.pnlUsd != null
          ? `${pair.pnlUsd >= 0 ? '+' : ''}$${Math.abs(pair.pnlUsd).toFixed(0)}` : '';
        const durStr = (() => {
          if (!pair.buyTrade?.time || !pair.sellTrade?.time) return null;
          const diff = new Date(pair.sellTrade.time) - new Date(pair.buyTrade.time);
          if (diff <= 0) return null;
          const mins = Math.floor(diff / 60000);
          const hrs  = Math.floor(mins / 60);
          const days = Math.floor(hrs / 24);
          if (days >= 1) return `${days}д ${hrs % 24}г`;
          if (hrs  >= 1) return `${hrs}г ${mins % 60}хв`;
          return `${mins}хв`;
        })();

        const label = document.createElement('div');
        label.className = 'trade-label';
        label.style.cssText = `
          position:absolute;left:${labelLeft}px;top:${labelTop}px;
          width:${LABEL_WIDTH}px;
          background:${color}ee;color:#fff;
          padding:5px 7px;border-radius:6px;font-size:10px;line-height:1.4;
          pointer-events:none;z-index:20;box-shadow:0 2px 8px rgba(0,0,0,0.22);
        `;
        label.innerHTML = isBuy ? `
          <div style="font-weight:700;font-size:10px;margin-bottom:2px">#${idx} BUY</div>
          <div style="font-size:10px">💰 ${Number(pair.buyTrade.price).toFixed(2)}</div>
          <div style="font-size:9px;opacity:0.85;margin-top:1px">📅 ${formatDateTime(pair.buyTrade.time)}</div>
        ` : `
          <div style="font-weight:700;font-size:10px;margin-bottom:2px">#${idx} SELL ${sign}${pair.pnl != null ? pair.pnl.toFixed(2) + '%' : ''}</div>
          <div style="font-size:10px">💰 ${Number(pair.sellTrade.price).toFixed(2)}</div>
          ${usdStr ? `<div style="font-size:10px;font-weight:600">${(pair.pnl ?? 0) >= 0 ? '📈' : '📉'} ${usdStr}</div>` : ''}
          <div style="font-size:9px;opacity:0.85">⏱ ${durStr || '—'}</div>
          <div style="font-size:9px;opacity:0.9;border-top:1px solid rgba(255,255,255,0.3);margin-top:2px;padding-top:2px">⚡ ${formatReasonShort(pair.reason)}</div>
        `;
        containerEl.appendChild(label);

        // Vertical dashed line from label bottom → chart point
        if (pointY !== null) {
          const labelH    = label.getBoundingClientRect().height;
          const lineTopY  = labelTop + labelH + 2;
          const dotCenterY = labelZoneHeight + pointY;
          const lineH      = Math.max(dotCenterY - lineTopY - 5, 0);

          if (lineH > 0) {
            const line = document.createElement('div');
            line.className = 'trade-line';
            line.style.cssText = `
              position:absolute;left:${x}px;top:${lineTopY}px;
              width:0;height:${lineH}px;
              border-left:2px dashed ${color};
              opacity:0.5;pointer-events:none;z-index:15;
            `;
            containerEl.appendChild(line);
          }

          // Dot at the trade point on the chart
          const DOT = 5;
          const dot = document.createElement('div');
          dot.className = 'trade-line';
          dot.style.cssText = `
            position:absolute;
            left:${x - DOT}px;top:${dotCenterY - DOT}px;
            width:${DOT * 2}px;height:${DOT * 2}px;
            border-radius:50%;background:${color};
            border:2px solid #fff;opacity:0.85;
            pointer-events:none;z-index:16;
          `;
          containerEl.appendChild(dot);
        }
      });
    };

    renderTradeLabelsRef.current = renderTradeLabels;
    if (tradeContext && tradePairs.length > 0) {
      renderTradeLabels();
      chart.timeScale().subscribeVisibleTimeRangeChange(() => {
        renderTradeLabelsRef.current?.();
      });
    }

    // ── Tooltip on crosshair ─────────────────────────────────────────────────
    if (tradeContext) {
      const toMs  = (t) => typeof t === 'number' ? t * 1000 : new Date(t).getTime();
      const barMs = chartType === '1m' ? 60 * 1000 * 3 : chartType === '5m' ? 5 * 60 * 1000 * 3 : isHourly ? 3600 * 1000 * 3 : 86400 * 1000 * 3;

      chart.subscribeCrosshairMove((param) => {
        const tooltip = tooltipRef.current;
        if (!tooltip) return;
        if (!param.time || !param.point) { tooltip.style.display = 'none'; return; }

        const near = markersRef.current.find(m =>
          Math.abs(toMs(m.time) - toMs(param.time)) <= barMs
        );
        if (!near?._data) { tooltip.style.display = 'none'; return; }

        const d       = near._data;
        const chartEl = chartContainerRef.current;

        if (d.action === 'BUY') {
          tooltip.innerHTML = `
            <div style="color:#22c55e;font-weight:700;margin-bottom:6px">▲ BUY #${d.idx}</div>
            <div>Ціна: <b>$${Number(d.price).toFixed(2)}</b></div>
            <div style="color:#94a3b8;margin-top:4px;font-size:11px">📅 ${formatDateTime(d.time)} <span style="opacity:0.7">ET</span> · ${formatKyivTime(d.time)} Київ</div>
          `;
        } else {
          const pnlColor = (d.pnl ?? 0) >= 0 ? '#22c55e' : '#ef4444';
          const sign     = (d.pnl ?? 0) >= 0 ? '+' : '';
          tooltip.innerHTML = `
            <div style="color:${pnlColor};font-weight:700;margin-bottom:6px">▼ SELL #${d.idx}</div>
            <div>Ціна продажу: <b>$${Number(d.price).toFixed(2)}</b></div>
            ${d.buyPrice != null ? `<div>Ціна купівлі: <b>$${Number(d.buyPrice).toFixed(2)}</b></div>` : ''}
            <div>P&L: <b style="color:${pnlColor}">${sign}${Number(d.pnl ?? 0).toFixed(2)}%${d.pnlUsd != null ? ` (${d.pnlUsd >= 0 ? '+' : ''}$${Math.abs(d.pnlUsd).toFixed(0)})` : ''}</b></div>
            ${d.duration ? `<div>Тривалість: ${d.duration}</div>` : ''}
            <div style="margin-top:6px;color:#94a3b8;font-size:11px">${formatReason(d.reason, d.pnl ?? 0)}</div>
            <div style="color:#94a3b8;margin-top:2px;font-size:11px">📅 ${formatDateTime(d.time)} <span style="opacity:0.7">ET</span> · ${formatKyivTime(d.time)} Київ</div>
          `;
        }

        const lzH = labelZoneHeightRef.current;
        let left = param.point.x + 15;
        let top  = lzH + param.point.y - 10;
        if (chartEl && left + 230 > chartEl.offsetWidth)  left = param.point.x - 235;
        if (chartEl && top  + 190 > chartEl.offsetHeight) top  = lzH + param.point.y - 195;
        tooltip.style.display = 'block';
        tooltip.style.left    = left + 'px';
        tooltip.style.top     = top  + 'px';
      });
    }

    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
        renderTradeLabelsRef.current?.();
      }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
      markersRef.current = [];
      chartContainerRef.current?.querySelectorAll('.trade-label, .trade-line').forEach(el => el.remove());
      if (labelZoneRef.current) { labelZoneRef.current.style.height = '0px'; }
      labelZoneHeightRef.current = 0;
      renderTradeLabelsRef.current = null;
      mainSeriesRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, displayType, showMA20, showMA50, showMA200, chartType, tradeContext, tradePairs, applyZoom]);

  // ── Zoom-only effect: re-applies zoom when preset changes WITHOUT rebuilding chart ──
  // (chart rebuild already applies zoom; this handles zoom changes on existing chart)
  useEffect(() => {
    if (!chartRef.current) return;
    const isHourly = isIntraday(chartType);
    applyZoom(chartRef.current, zoomPreset, isHourly, filteredDataRef.current);
  }, [zoomPreset, chartType, applyZoom]);

  // ── Show/hide trade labels ─────────────────────────────────────────────────
  useEffect(() => {
    if (!showLabels) {
      chartContainerRef.current?.querySelectorAll('.trade-label, .trade-line').forEach(el => el.remove());
      if (labelZoneRef.current) labelZoneRef.current.style.height = '0px';
      labelZoneHeightRef.current = 0;
    } else {
      renderTradeLabelsRef.current?.();
    }
  }, [showLabels]);

  // ── Period / zoom button handlers ─────────────────────────────────────────
  function handlePeriodClick(p) {
    if (hasTradeCtx) {
      // In trade mode: only change the zoom, don't re-filter data
      setZoomPreset(p);
    } else {
      // Normal mode: filter data + zoom
      setPeriod(p);
      setZoomPreset(p);
    }
  }

  function handleTradeZoom() {
    setZoomPreset('trade');
  }

  const hasHourly  = !!ticker.hourly?.has_data;
  const hasDaily   = !!ticker.daily?.has_data;
  const has5m      = !!ticker.minute_5m?.has_data;
  const has1m      = !!ticker.minute_1m?.has_data;

  // Active period button: in trade mode, highlight based on zoomPreset
  const activePeriod = hasTradeCtx ? zoomPreset : period;
  const chartLabel   = hasTradeCtx
    ? (zoomPreset === 'trade' ? 'Угода' : zoomPreset === 'period' ? 'Період' : zoomPreset)
    : period;

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={S.header}>
          {onBack && <button style={S.backBtn} onClick={onBack}>← Назад</button>}
          <span style={S.tickerBadge}>{ticker.ticker}</span>
          <span style={S.companyName}>{ticker.name}</span>
          {ticker.index  && <span style={S.tagBadge('green')}>{ticker.index}</span>}
          {ticker.sector && <span style={S.tagBadge('blue')}>{ticker.sector}</span>}
          {/* Бектест одного тікера є лише в бектестингу (calc); у застосунку
              бота маршруту /single-backtest немає — кнопка лише міняла адресу. */}
          {process.env.REACT_APP_VARIANT !== 'trading' && <button
            style={{
              marginLeft: 'auto', padding: '5px 14px',
              background: '#2563EB', color: '#fff', border: 'none',
              borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
            onClick={() => window.dispatchEvent(
              new CustomEvent('goto:single-backtest', { detail: { ticker: ticker.ticker } })
            )}
          >
            ⚡ Backtest
          </button>}
        </div>

        {/* ── Trade info card OR hourly/daily metadata (hidden in compact bot view) ── */}
        {compact ? null : tradeContext ? (() => {
          if (tradePairs.length > 0) {
            // Multi-trade summary card
            const closed   = tradePairs.filter(p => p.sellTrade);
            const wins     = closed.filter(p => p.pnl != null && p.pnl > 0).length;
            const totalPnl = closed.reduce((s, p) => s + (p.pnl || 0), 0);
            const winPct   = closed.length ? Math.round(wins / closed.length * 100) : 0;
            const openCnt  = tradePairs.filter(p => !p.sellTrade).length;
            return (
              <div style={{
                background: '#fff', border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${C.primary}`,
                borderRadius: 10, padding: '14px 20px', marginBottom: 16, boxShadow: C.shadow,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.textMd, marginBottom: 4 }}>
                  📊 {ticker.ticker} — {tradePairs.length} угод з бектесту
                </div>
                {tradeContext?.strategyParams && (
                  <div style={{ marginBottom: 10 }}>
                    <StrategyParamsLabel params={tradeContext.strategyParams} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 13 }}>
                  <div>
                    <span style={{ color: C.textSm }}>Закриті: </span>
                    <b>{closed.length}</b>
                    {openCnt > 0 && <span style={{ color: C.textSm }}> · відкриті: <b>{openCnt}</b></span>}
                  </div>
                  <div>
                    <span style={{ color: C.textSm }}>Win rate: </span>
                    <b style={{ color: winPct >= 60 ? C.success : winPct >= 40 ? C.text : C.danger }}>
                      {wins}/{closed.length} ({winPct}%)
                    </b>
                  </div>
                  <div>
                    <span style={{ color: C.textSm }}>Total P&L: </span>
                    <b style={{ color: totalPnl >= 0 ? C.success : C.danger }}>
                      {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}%
                    </b>
                  </div>
                  <div style={{ color: C.textSm, fontSize: 12, alignSelf: 'center' }}>
                    Натисніть рядок у таблиці нижче → зум на угоду
                  </div>
                </div>
              </div>
            );
          }
          // Single-trade info card (backward compat)
          const buy  = tradeContext.buyTrade;
          const sell = tradeContext.sellTrade;
          const pnlPct = sell?.pnl_pct;
          const pnlUsd = sell?.change_usd;
          const pnlPos = pnlPct != null && pnlPct >= 0;
          const duration = (buy?.time && sell?.time) ? (() => {
            const diff = new Date(sell.time) - new Date(buy.time);
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            return d > 0 ? `${d}д ${h}г` : `${h}г`;
          })() : null;
          return (
            <div style={{
              background: '#fff', border: `1px solid ${C.border}`,
              borderLeft: `4px solid ${pnlPos ? C.success : pnlPct == null ? C.primary : C.danger}`,
              borderRadius: 10, padding: '14px 20px', marginBottom: 16, boxShadow: C.shadow,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textMd, marginBottom: 10 }}>
                📈 {ticker.ticker} — угода з бектесту
              </div>
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 13 }}>
                {buy && (
                  <div>
                    <span style={{ color: C.textSm }}>BUY: </span>
                    <b style={{ color: C.success }}>{buy.time?.slice(0, 10)}</b>
                    {buy.price != null && <span style={{ color: C.textMd }}> · ${Number(buy.price).toFixed(2)}</span>}
                  </div>
                )}
                {sell && (
                  <div>
                    <span style={{ color: C.textSm }}>SELL: </span>
                    <b style={{ color: C.danger }}>{sell.time?.slice(0, 10)}</b>
                    {sell.price != null && <span style={{ color: C.textMd }}> · ${Number(sell.price).toFixed(2)}</span>}
                  </div>
                )}
                {pnlPct != null && (
                  <div>
                    <span style={{ color: C.textSm }}>P&L: </span>
                    <b style={{ color: pnlPos ? C.success : C.danger }}>
                      {pnlPct >= 0 ? '+' : ''}{Number(pnlPct).toFixed(2)}%
                      {pnlUsd != null && ` (${pnlUsd >= 0 ? '+' : ''}$${Math.abs(Number(pnlUsd)).toFixed(0)})`}
                    </b>
                  </div>
                )}
                {duration && (
                  <div>
                    <span style={{ color: C.textSm }}>Duration: </span>
                    <b>{duration}</b>
                  </div>
                )}
                {sell?.description && (
                  <div>
                    <span style={{ color: C.textSm }}>Причина: </span>
                    <span style={{ color: C.textMd }}>{sell.description}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })() : (
          <>
            {/* Data availability — 4 cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              {[
                { key: 'daily',  label: '📅 Daily',  color: '#16A34A', dark: '#15803D', data: ticker.daily,      has: hasDaily   },
                { key: 'hourly', label: '📊 Hourly', color: '#2563EB', dark: '#1D4ED8', data: ticker.hourly,     has: hasHourly  },
                { key: '5m',     label: '⚡ 5min',   color: '#D97706', dark: '#92400E', data: ticker.minute_5m,  has: has5m      },
                { key: '1m',     label: '⚡ 1min',   color: '#DC2626', dark: '#991B1B', data: ticker.minute_1m,  has: has1m      },
              ].map(({ key, label, color, dark, data, has }) => (
                <div key={key} style={S.infoCard(has ? color : C.border)}>
                  <div style={S.infoTitle(has ? dark : C.textSm)}>{label}</div>
                  {has ? (
                    <>
                      <div style={{ ...S.infoBody, fontSize: 12 }}>{data.date_from} → {data.date_to}</div>
                      <div style={S.infoSub}>{(data.bars || 0).toLocaleString()} барів</div>
                    </>
                  ) : (
                    <div style={{ ...S.infoBody, color: C.textSm, fontSize: 12 }}>Немає даних</div>
                  )}
                </div>
              ))}
            </div>

            {/* Company info from ticker_stats */}
            {companyInfo && (
              <div style={{
                background: '#FAFBFF', border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '12px 16px', marginBottom: 12, display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px 20px',
              }}>
                {[
                  { label: 'Ринк. кап.',   value: companyInfo.market_cap != null ? `$${(companyInfo.market_cap / 1e9).toFixed(2)}B` : null },
                  { label: 'Поточна ціна', value: companyInfo.current_price != null ? `$${companyInfo.current_price.toFixed(2)}` : null },
                  { label: 'Галузь',       value: companyInfo.industry || null },
                  { label: 'Біржа',        value: companyInfo.exchange || null },
                  { label: 'P/E (TTM)',    value: companyInfo.pe_trailing != null ? companyInfo.pe_trailing.toFixed(1) : null },
                  { label: 'P/E (fwd)',    value: companyInfo.pe_forward != null ? companyInfo.pe_forward.toFixed(1) : null },
                  { label: 'Beta',         value: companyInfo.beta != null ? companyInfo.beta.toFixed(2) : null },
                  { label: 'P/B',          value: companyInfo.price_to_book != null ? companyInfo.price_to_book.toFixed(2) : null },
                  { label: 'ROE',          value: companyInfo.roe != null ? `${(companyInfo.roe * 100).toFixed(1)}%` : null },
                  { label: 'Маржа',        value: companyInfo.profit_margin != null ? `${(companyInfo.profit_margin * 100).toFixed(1)}%` : null },
                  { label: '52w Low',      value: companyInfo['52w_low'] != null ? `$${companyInfo['52w_low'].toFixed(2)}` : null },
                  { label: '52w High',     value: companyInfo['52w_high'] != null ? `$${companyInfo['52w_high'].toFixed(2)}` : null },
                  { label: 'EPS (TTM)',    value: companyInfo.eps_ttm != null ? `$${companyInfo.eps_ttm.toFixed(2)}` : null },
                  { label: 'Виручка',      value: companyInfo.revenue_ttm != null ? `$${(companyInfo.revenue_ttm / 1e9).toFixed(1)}B` : null },
                  { label: 'Зростання вир.', value: companyInfo.revenue_growth != null ? `${(companyInfo.revenue_growth * 100).toFixed(1)}%` : null },
                  { label: 'Зрост. прибутку', value: companyInfo.earnings_growth != null ? `${(companyInfo.earnings_growth * 100).toFixed(1)}%` : null },
                ].filter(item => item.value != null).map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 1 }}>{value}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Control bar ─────────────────────────────────────────────── */}
        <div style={S.controlBar}>
          {/* Data type: Daily / Hourly / 5m / 1m (hidden in compact bot view) */}
          {!compact && (
            <div style={S.switcher}>
              {[
                ['daily',  '📅 Daily',  hasDaily],
                ['hourly', '📊 Hourly', hasHourly],
                ['5m',     '⚡ 5min',   has5m],
                ['1m',     '⚡ 1min',   has1m],
              ].map(([t, lbl, enabled]) => (
                <button
                  key={t}
                  style={enabled ? S.switchBtn(chartType === t) : S.switchBtnDisabled}
                  disabled={!enabled}
                  onClick={() => enabled && setChartType(t)}
                  title={enabled ? '' : 'Дані недоступні'}
                >
                  {lbl}
                </button>
              ))}
            </div>
          )}

          {/* Candlestick / Line (hidden in compact bot view) */}
          {!compact && (
            <div style={S.switcher}>
              <button style={S.switchBtn(displayType === 'candlestick')} onClick={() => setDisplayType('candlestick')}>🕯 Candlestick</button>
              <button style={S.switchBtn(displayType === 'line')}        onClick={() => setDisplayType('line')}>📈 Line</button>
            </div>
          )}

          {/* Period buttons (hidden in compact bot view) */}
          {!compact && (
            <div style={{ display: 'flex', gap: 4 }}>
              {PERIODS.map(p => (
                <button key={p} style={S.periodBtn(activePeriod === p)} onClick={() => handlePeriodClick(p)}>{p}</button>
              ))}
            </div>
          )}

          {/* 🗓 Period / 📍 Trade zoom buttons — only in trade context */}
          {hasTradeCtx && (
            <button style={S.tradeBtn(zoomPreset === 'period')} onClick={() => setZoomPreset('period')}>
              🗓 Період
            </button>
          )}
          {hasTradeCtx && (
            <button style={S.tradeBtn(zoomPreset === 'trade')} onClick={handleTradeZoom}>
              📍 Угода
            </button>
          )}
        </div>

        {/* ── Chart card ──────────────────────────────────────────────── */}
        <div style={S.card} ref={chartCardRef}>
          <div style={S.chartHeader}>
            <span style={S.chartTitle}>
              {ticker.ticker} · {chartType === 'daily' ? 'Daily' : chartType === 'hourly' ? 'Hourly' : chartType === '5m' ? '5min' : '1min'} · {chartLabel}
            </span>
            <div style={S.maRow}>
              {!compact && [
                { label: 'MA20',  color: '#F59E0B', state: showMA20,  set: setShowMA20  },
                { label: 'MA50',  color: '#8B5CF6', state: showMA50,  set: setShowMA50  },
                { label: 'MA200', color: '#EF4444', state: showMA200, set: setShowMA200 },
              ].map(({ label, color, state, set }) => (
                <label key={label} style={S.maLabel(color)} onClick={() => set(v => !v)}>
                  <input type="checkbox" checked={state} onChange={() => {}} style={{ accentColor: color, cursor: 'pointer' }} />
                  <span style={S.maDot(state ? color : C.borderMd)} />
                  {label}
                </label>
              ))}
              {tradeContext && tradePairs.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: C.textMd, marginLeft: 8 }}>
                  <input
                    type="checkbox"
                    checked={showLabels}
                    onChange={e => setShowLabels(e.target.checked)}
                    style={{ width: 13, height: 13, accentColor: C.primary, cursor: 'pointer' }}
                  />
                  Підписи угод
                </label>
              )}
            </div>
          </div>

          <div ref={chartContainerRef} style={S.chartContainer}>
            {/* Zone for trade labels — height grows dynamically */}
            <div ref={labelZoneRef} style={{ width: '100%', minHeight: 0, position: 'relative', flexShrink: 0 }} />

            {/* Actual lightweight-charts canvas */}
            <div ref={chartInnerRef} style={{ position: 'relative', height: 420 }} />

            {/* Crosshair tooltip */}
            <div ref={tooltipRef} style={{
              position: 'absolute', display: 'none', zIndex: 100,
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '10px 14px',
              fontSize: 12, color: '#fff', pointerEvents: 'none',
              minWidth: 210, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            }} />

            {loading && <div style={S.loadingOverlay}>Завантаження даних…</div>}
            {!loading && error && <div style={S.errorMsg}>{error}</div>}
            {!loading && !error && filteredData.length === 0 && rawData.length === 0 && (
              <div style={S.errorMsg}>Немає даних для відображення</div>
            )}
            {!loading && !error && filteredData.length === 0 && rawData.length > 0 && (
              <div style={S.errorMsg}>Немає даних за обраний період. Спробуйте ширший фільтр.</div>
            )}
          </div>

          {filteredData.length > 0 && (
            <div style={{ padding: '8px 16px', fontSize: 12, color: C.textSm, borderTop: `1px solid ${C.border}` }}>
              {filteredData.length.toLocaleString()} барів · Volume bars · {chartType === 'daily' ? 'Денні дані' : `Час: ET (${chartType === '5m' ? '5-хв' : chartType === '1m' ? '1-хв' : 'Годинні'})`}
            </div>
          )}
        </div>

      </div>

      {/* ── Trade pairs panel ───────────────────────────────────── */}
      {tradePairs.length > 0 && (() => {
        const closed   = tradePairs.filter(p => p.sellTrade);
        const wins     = closed.filter(p => p.pnl != null && p.pnl > 0).length;
        const totalPnl = closed.reduce((s, p) => s + (p.pnl || 0), 0);

        const fmtDur = (buyTime, sellTime) => {
          if (!buyTime || !sellTime) return '—';
          const diff = new Date(sellTime) - new Date(buyTime);
          if (diff <= 0) return '—';
          const mins = Math.floor(diff / 60000);
          const hrs  = Math.floor(mins / 60);
          const days = Math.floor(hrs / 24);
          if (days >= 1) return `${days}д ${hrs % 24}г`;
          if (hrs  >= 1) return `${hrs}г ${mins % 60}хв`;
          return `${mins}хв`;
        };

        const toHuman = (bars, dtype) => {
          if (!bars) return '';
          if (dtype === 'daily')  return `${bars}д`;
          if (dtype === 'hourly') return `${bars}г`;
          return `${bars}б`;
        };

        const formatBuyReason = (pair) => {
          const pct      = pair.buyTrade?.buy_signal_pct;
          const bars     = pair.buyTrade?.buy_growth_bars;
          const refPrice = pair.buyTrade?.buy_ref_price;
          const sp       = tradeContext?.strategyParams;
          const xBuy     = sp?.x_buy;
          const tBuyH    = sp?.t_hours_buy ?? sp?.buy_window_days;
          const tBuyStr  = tBuyH != null
            ? (sp?.data_type === 'hourly'
                ? (Number(tBuyH) >= 24 ? `${Math.round(Number(tBuyH) / 24)}д` : `${tBuyH}г`)
                : `${tBuyH}д`)
            : null;
          return (
            <div>
              <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 12 }}>
                Зріст {pct != null ? `+${Number(pct).toFixed(2)}%` : ''}{xBuy != null ? ` (≥${xBuy}%)` : ''}
              </div>
              {bars != null && (
                <div style={{ fontSize: 11, color: C.textSm }}>
                  за {toHuman(bars, tradeContext?.dataType)}{tBuyStr ? ` (≥${tBuyStr})` : ''}
                </div>
              )}
              {refPrice != null && (
                <div style={{ fontSize: 11, color: C.textSm }}>з ${Number(refPrice).toFixed(2)}</div>
              )}
            </div>
          );
        };

        const formatSellReason = (pair) => {
          const reason   = pair.reason || '';
          const dropPct  = pair.sellTrade?.sell_drop_pct;
          const totalPnl = pair.pnl;
          const pnlColor = (totalPnl || 0) >= 0 ? '#22c55e' : '#ef4444';
          const pnlSign  = (totalPnl || 0) >= 0 ? '+' : '';
          const pnlStr   = totalPnl != null ? `${pnlSign}${Number(totalPnl).toFixed(2)}%` : null;
          const sp      = tradeContext?.strategyParams;
          const dtype   = tradeContext?.dataType;
          const hBars = (b) => toHuman(b, dtype);
          const durBars  = pair.sellTrade?.duration_bars;
          const dur      = durBars != null ? hBars(durBars) : null;
          const tSellStr = (() => {
            const v = sp?.sell_window_days ?? sp?.t_hours_sell ?? sp?.t_hours_buy ?? sp?.buy_window_days;
            if (v == null) return null;
            if (dtype === 'hourly') return Number(v) >= 24 ? `${Math.round(Number(v) / 24)}д` : `${v}г`;
            return `${v}д`;
          })();
          const tBuyStr = (() => {
            const v = sp?.t_hours_buy ?? sp?.buy_window_days;
            if (v == null) return null;
            if (dtype === 'hourly') return Number(v) >= 24 ? `${Math.round(Number(v) / 24)}д` : `${v}г`;
            return `${v}д`;
          })();
          switch (reason) {
            case 'drop_in_period':
            case 'stop_loss':
              return (
                <div>
                  <div style={{ color: '#ef4444', fontWeight: 600, fontSize: 12 }}>
                    Падіння {dropPct != null ? `${Math.abs(Number(dropPct)).toFixed(2)}%` : (pnlStr ?? '')}
                    {sp?.y_sell != null ? ` (≥${sp.y_sell}%)` : ''}
                  </div>
                  {dur != null && (
                    <div style={{ fontSize: 11, color: C.textSm }}>
                      за {dur}{tSellStr ? ` (≤${tSellStr})` : ''}
                    </div>
                  )}
                </div>
              );
            case 'hard_stop_loss':
              return (
                <div>
                  <div style={{ color: '#dc2626', fontWeight: 600, fontSize: 12 }}>
                    Hard Stop{sp?.hard_stop_pct != null ? ` (≤−${sp.hard_stop_pct}%)` : ''}
                  </div>
                  {pnlStr != null && (
                    <div style={{ fontSize: 11, color: pnlColor, fontWeight: 600 }}>{pnlStr} тримали {dur ?? '?'}</div>
                  )}
                </div>
              );
            case 'no_growth':
              return (
                <div>
                  <div style={{ color: '#f59e0b', fontWeight: 600, fontSize: 12 }}>
                    Слабкий ріст{pnlStr != null ? ` ${pnlStr}` : ''}
                    {sp?.z_no_growth != null && sp.z_no_growth < 900 ? ` (<${sp.z_no_growth}%)` : ''}
                  </div>
                  {dur != null && (
                    <div style={{ fontSize: 11, color: C.textSm }}>
                      тримали {dur}{tBuyStr ? ` (≥${tBuyStr})` : ''}
                    </div>
                  )}
                </div>
              );
            case 'stagnation': {
              const sMin = sp?.stagnation_min_pct;
              const sMax = sp?.stagnation_max_pct;
              const sBars = sp?.stagnation_bars;
              return (
                <div>
                  <div style={{ color: '#f59e0b', fontWeight: 600, fontSize: 12 }}>Стагнація</div>
                  {(sMin != null || sMax != null || sBars != null) ? (
                    <div style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                      ({sMin ?? '?'}%..{sMax ?? '?'}%{sBars != null ? ` за ${hBars(sBars)}` : ''})
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: C.textSm }}>ціна не рухалась</div>
                  )}
                </div>
              );
            }
            case 'overnight_protection':
            case 'risk_protection':
              return (
                <div>
                  <div style={{ color: '#8b5cf6', fontWeight: 600, fontSize: 12 }}>Ніч/вихідні</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>захист від гепів</div>
                </div>
              );
            case 'take_profit':
              return (
                <div>
                  <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 12 }}>
                    Take-profit{(sp?.take_profit_pct ?? sp?.tp_pct) != null ? ` (≥${sp.take_profit_pct ?? sp.tp_pct}%)` : ''}
                  </div>
                  {pnlStr != null && (
                    <div style={{ fontSize: 11, color: pnlColor, fontWeight: 600 }}>{pnlStr} тримали {dur ?? '?'}</div>
                  )}
                </div>
              );
            case 'end_of_period':
              return (
                <div>
                  <div style={{ color: '#64748b', fontWeight: 600, fontSize: 12 }}>Кінець бектесту</div>
                  {dur != null && <div style={{ fontSize: 11, color: C.textSm }}>тримали {dur}</div>}
                </div>
              );
            case 'regime_change':
              return <div style={{ color: '#64748b', fontSize: 12 }}>Зміна режиму</div>;
            default:
              return <div style={{ color: C.textSm, fontSize: 12 }}>{reason || '—'}</div>;
          }
        };

        const thS = { padding: '9px 12px', background: '#F8FAFC', border: `1px solid ${C.border}`, color: C.textMd, fontWeight: 600, fontSize: 12, textAlign: 'left', whiteSpace: 'nowrap' };
        const tdS = (extra) => ({ padding: '8px 12px', border: `1px solid ${C.border}`, fontSize: 12, whiteSpace: 'nowrap', ...(extra || {}) });

        const navBtn = (disabled) => ({
          padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
          border: `1px solid ${C.border}`, borderRadius: 6,
          background: disabled ? C.pageBg : C.cardBg, color: disabled ? C.textSm : C.primary,
          opacity: disabled ? 0.5 : 1,
        });

        // ── Sort pairs ────────────────────────────────────────────────────
        const getMs = (p) => {
          if (!p.buyTrade?.time || !p.sellTrade?.time) return Infinity;
          const d = new Date(p.sellTrade.time) - new Date(p.buyTrade.time);
          return d > 0 ? d : Infinity;
        };
        const sortedPairs = tradePairs.map((p, i) => ({ ...p, _origIdx: i })).sort((a, b) => {
          let valA, valB;
          switch (sortCol) {
            case 'id':       valA = a._origIdx; valB = b._origIdx; break;
            case 'buy_time': valA = new Date(a.buyTrade?.time  || 0).getTime(); valB = new Date(b.buyTrade?.time  || 0).getTime(); break;
            case 'sell_time':
              valA = a.sellTrade ? new Date(a.sellTrade.time || 0).getTime() : Infinity;
              valB = b.sellTrade ? new Date(b.sellTrade.time || 0).getTime() : Infinity;
              break;
            case 'pnl_pct':  valA = parseFloat(a.pnl    ?? 0); valB = parseFloat(b.pnl    ?? 0); break;
            case 'pnl_usd':  valA = parseFloat(a.pnlUsd ?? 0); valB = parseFloat(b.pnlUsd ?? 0); break;
            case 'duration': valA = getMs(a); valB = getMs(b); break;
            default: return 0;
          }
          if (valA === Infinity && valB === Infinity) return 0;
          if (valA === Infinity) return 1;
          if (valB === Infinity) return -1;
          return sortDir === 'asc' ? valA - valB : valB - valA;
        });

        const SortTh = ({ col, label, style: extraStyle = {} }) => {
          const isActive = sortCol === col;
          const icon = !isActive ? '↕' : sortDir === 'asc' ? '↑' : '↓';
          return (
            <th onClick={() => handleSort(col)} style={{
              cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
              padding: '8px 12px', textAlign: 'left',
              background: isActive ? `${C.primary}15` : '#F8FAFC',
              color: isActive ? C.primary : C.textMd,
              fontWeight: 600, fontSize: 12,
              border: `1px solid ${C.border}`,
              borderBottom: `2px solid ${isActive ? C.primary : C.border}`,
              ...extraStyle,
            }}>
              {label}{' '}
              <span style={{ opacity: isActive ? 1 : 0.3, fontSize: 10 }}>{icon}</span>
            </th>
          );
        };

        const PAIRS_PAGE_OPTS = [10, 50, 100, 500, 1000, 'all'];
        const totalPairsPages = pairsPageSize === 'all' ? 1 : Math.ceil(sortedPairs.length / pairsPageSize);
        const displayedPairs  = pairsPageSize === 'all'
          ? sortedPairs
          : sortedPairs.slice(pairsPage * pairsPageSize, (pairsPage + 1) * pairsPageSize);
        const paginBtnStyle = (disabled) => ({
          padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`,
          background: 'transparent', color: disabled ? C.textSm : C.text,
          cursor: disabled ? 'default' : 'pointer', fontSize: 12, opacity: disabled ? 0.4 : 1,
        });

        return (
          <div style={{ ...S.card, marginTop: 16, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                📋 {ticker.ticker} — {tradePairs.length} угод
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  style={navBtn(activePairIdx <= 0)}
                  disabled={activePairIdx <= 0}
                  onClick={() => zoomToPair(activePairIdx - 1, tradePairs)}
                >← Угода</button>
                <span style={{ fontSize: 12, color: C.textMd, minWidth: 60, textAlign: 'center' }}>
                  {activePairIdx >= 0 ? `${activePairIdx + 1} / ${tradePairs.length}` : `— / ${tradePairs.length}`}
                </span>
                <button
                  style={navBtn(activePairIdx >= tradePairs.length - 1)}
                  disabled={activePairIdx >= tradePairs.length - 1}
                  onClick={() => zoomToPair(activePairIdx + 1, tradePairs)}
                >Угода →</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <SortTh col="id"        label="#"           />
                    <SortTh col="buy_time"  label="BUY"         />
                    <th style={{ ...thS, minWidth: 140 }}>Причина BUY</th>
                    <SortTh col="sell_time" label="SELL"        />
                    <th style={{ ...thS, minWidth: 140 }}>Причина SELL</th>
                    <SortTh col="pnl_pct"   label="P&L %"      />
                    <SortTh col="pnl_usd"   label="P&L $"      />
                    <SortTh col="duration"  label="Тривалість" />
                  </tr>
                </thead>
                <tbody>
                  {displayedPairs.map((pair, idx) => {
                    const origIdx   = pair._origIdx;
                    const displayNum = pairsPageSize === 'all' ? idx + 1 : pairsPage * pairsPageSize + idx + 1;
                    const isActive = origIdx === activePairIdx;
                    const pnlOk  = pair.pnl != null && pair.pnl > 0;
                    const pnlBad = pair.pnl != null && pair.pnl < 0;
                    const rowBg  = isActive ? '#EFF6FF'
                                 : pnlOk   ? '#F0FDF4'
                                 : pnlBad  ? '#FEF2F2'
                                 : '#FFFFFF';
                    return (
                      <tr
                        key={origIdx}
                        style={{ background: rowBg, cursor: 'pointer', outline: isActive ? `2px solid ${C.primary}` : 'none', outlineOffset: -1 }}
                        onClick={() => {
                          zoomToPair(origIdx, tradePairs);
                          chartCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        <td style={tdS({ fontWeight: 600, color: C.textMd })}>{displayNum}</td>
                        <td style={tdS()}>
                          <span style={{ color: C.success, fontWeight: 600 }}>
                            {formatDateTime(pair.buyTrade.time)} <span style={{ color: C.textSm, fontSize: 11 }}>ET</span>
                          </span>
                          {pair.buyTrade.price != null && (
                            <span style={{ color: C.textMd }}> · ${Number(pair.buyTrade.price).toFixed(2)}</span>
                          )}
                        </td>
                        <td style={tdS({ minWidth: 140, verticalAlign: 'top' })}>
                          {formatBuyReason(pair)}
                        </td>
                        <td style={tdS()}>
                          {pair.sellTrade ? (
                            <>
                              <span style={{ color: C.danger, fontWeight: 600 }}>
                                {formatDateTime(pair.sellTrade.time)} <span style={{ color: C.textSm, fontSize: 11 }}>ET</span>
                              </span>
                              {pair.sellTrade.price != null && (
                                <span style={{ color: C.textMd }}> · ${Number(pair.sellTrade.price).toFixed(2)}</span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: C.textSm }}>відкрита</span>
                          )}
                        </td>
                        <td style={tdS({ minWidth: 140, verticalAlign: 'top' })}>
                          {pair.sellTrade
                            ? formatSellReason(pair)
                            : <span style={{ color: C.textSm, fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={tdS({ fontWeight: 700, color: pnlOk ? C.success : pnlBad ? C.danger : C.textMd })}>
                          {pair.pnl != null ? `${pair.pnl >= 0 ? '+' : ''}${Number(pair.pnl).toFixed(2)}%` : '—'}
                        </td>
                        <td style={tdS({ fontWeight: 600, color: pair.pnlUsd != null && pair.pnlUsd > 0 ? C.success : pair.pnlUsd != null && pair.pnlUsd < 0 ? C.danger : C.textMd })}>
                          {pair.pnlUsd != null
                            ? `${pair.pnlUsd >= 0 ? '+' : ''}$${Math.abs(Number(pair.pnlUsd)).toFixed(2)}`
                            : '—'}
                        </td>
                        <td style={tdS({ color: C.textMd })}>
                          {fmtDur(pair.buyTrade.time, pair.sellTrade?.time)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 16px', borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 8,
            }}>
              <div style={{ fontSize: 12, color: C.textSm }}>
                {pairsPageSize === 'all'
                  ? `All ${sortedPairs.length} угод`
                  : `${pairsPage * pairsPageSize + 1}–${Math.min((pairsPage + 1) * pairsPageSize, sortedPairs.length)} of ${sortedPairs.length}`}
              </div>
              {pairsPageSize !== 'all' && totalPairsPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setPairsPage(0)} disabled={pairsPage === 0} style={paginBtnStyle(pairsPage === 0)}>«</button>
                  <button onClick={() => setPairsPage(p => Math.max(0, p - 1))} disabled={pairsPage === 0} style={paginBtnStyle(pairsPage === 0)}>‹</button>
                  {Array.from({ length: Math.min(5, totalPairsPages) }, (_, i) => {
                    let start = Math.max(0, pairsPage - 2);
                    let end   = Math.min(totalPairsPages - 1, start + 4);
                    start = Math.max(0, end - 4);
                    return start + i;
                  }).filter(p => p < totalPairsPages).map(p => (
                    <button key={p} onClick={() => setPairsPage(p)} style={{
                      ...paginBtnStyle(false),
                      background: p === pairsPage ? C.primary : 'transparent',
                      color:      p === pairsPage ? '#fff'    : C.text,
                      fontWeight: p === pairsPage ? 700       : 400,
                      minWidth:   30,
                    }}>{p + 1}</button>
                  ))}
                  <button onClick={() => setPairsPage(p => Math.min(totalPairsPages - 1, p + 1))} disabled={pairsPage >= totalPairsPages - 1} style={paginBtnStyle(pairsPage >= totalPairsPages - 1)}>›</button>
                  <button onClick={() => setPairsPage(totalPairsPages - 1)} disabled={pairsPage >= totalPairsPages - 1} style={paginBtnStyle(pairsPage >= totalPairsPages - 1)}>»</button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: C.textSm }}>Rows:</span>
                <div style={{ display: 'flex', gap: 3 }}>
                  {PAIRS_PAGE_OPTS.map(opt => (
                    <button key={opt} onClick={() => { setPairsPageSize(opt === 'all' ? 'all' : +opt); setPairsPage(0); }} style={{
                      padding: '2px 8px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                      border:      `1px solid ${pairsPageSize === (opt === 'all' ? 'all' : +opt) ? C.primary : C.border}`,
                      background:  pairsPageSize === (opt === 'all' ? 'all' : +opt) ? C.primary : 'transparent',
                      color:       pairsPageSize === (opt === 'all' ? 'all' : +opt) ? '#fff' : C.text,
                      fontWeight:  pairsPageSize === (opt === 'all' ? 'all' : +opt) ? 700 : 400,
                    }}>
                      {opt === 'all' ? 'All' : opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {summary && (
              <div style={{ borderTop: `2px solid ${C.border}`, padding: '14px 16px', background: '#F8FAFC' }}>
                {/* Row 1: key numbers */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[
                    {
                      label: 'Загальний P&L',
                      value: `${summary.totalPnlUsd >= 0 ? '+' : ''}$${Math.abs(summary.totalPnlUsd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      color: summary.totalPnlUsd >= 0 ? '#22c55e' : '#ef4444',
                      big: true,
                    },
                    {
                      label: 'Середній P&L %',
                      value: `${summary.avgPnlPct >= 0 ? '+' : ''}${summary.avgPnlPct.toFixed(2)}%`,
                      color: summary.avgPnlPct >= 0 ? '#22c55e' : '#ef4444',
                    },
                    {
                      label: 'Win / Loss',
                      value: `${summary.wins} / ${summary.losses}`,
                      color: C.text,
                    },
                  ].map(stat => (
                    <div key={stat.label}>
                      <div style={{ fontSize: 11, color: C.textSm, marginBottom: 2 }}>{stat.label}</div>
                      <div style={{ fontWeight: 700, fontSize: stat.big ? 18 : 14, color: stat.color }}>{stat.value}</div>
                    </div>
                  ))}
                </div>
                {/* Row 2: durations */}
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Середня тривалість', value: summary.avgDur },
                    { label: 'Найдовша угода',     value: summary.maxDur },
                    { label: 'Найкоротша угода',   value: summary.minDur },
                  ].map(stat => (
                    <div key={stat.label}>
                      <div style={{ fontSize: 11, color: C.textSm, marginBottom: 2 }}>{stat.label}</div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
}
